/**
 * Cross-Sell i18n bundle (pt-BR + en-US).
 *
 * Registered as a deep merge into the existing `translation` namespace (same
 * pattern as `shared/i18n/wave-e.ts`) so we don't touch the giant locale JSONs.
 * Idempotent — `registerCrossSellTranslations()` is called once from App.tsx.
 */
import i18n from 'i18next';

export const CROSS_SELL_PT_BR = {
  crossSell: {
    title: 'Cross-Sell',
    sidebar: 'Importar clientes',
    nav: {
      import: 'Importar base',
    },
    campaignsSidebar: 'Campanhas',
    catalogSidebar: 'Importar catálogo',
    campaigns: {
      // List
      title: 'Campanhas de cross-sell',
      subtitle: 'Crie e acompanhe campanhas que oferecem produtos para sua base ativa.',
      newButton: 'Nova campanha',
      empty: 'Nenhuma campanha ainda. Crie a primeira para começar a oferecer.',
      columns: {
        name: 'Nome',
        product: 'Produto',
        status: 'Status',
        reach: 'Alcance',
        members: 'Membros',
        createdBy: 'Criada por',
        when: 'Criada em',
      },
      status: {
        draft: 'Rascunho',
        active: 'Ativa',
        paused: 'Pausada',
        completed: 'Concluída',
        cancelled: 'Cancelada',
      },
      // Wizard (UX §4.7)
      wizard: {
        title: 'Nova campanha de cross-sell',
        subtitle: 'Defina o produto, o público e a política de outreach.',
        nameLabel: '1. Nome da campanha',
        namePlaceholder: 'Cross-sell Janus SDR Voice Q3 2026',
        nameRequired: 'Dê um nome para a campanha.',
        descriptionLabel: 'Descrição (opcional)',
        productLabel: '2. Produto a oferecer',
        productPlaceholder: 'Selecione um produto do catálogo',
        productRequired: 'Escolha o produto que será oferecido.',
        productCatalogEmpty:
          'Você ainda não tem produtos no catálogo. Cadastre seus produtos primeiro.',
        productCatalogCta: 'Ir para o catálogo',
        filtersLabel: '3. Filtros de elegibilidade',
        porteLabel: 'Porte',
        portePlaceholder: 'Pequeno (P) · Médio (M) · Grande (G)',
        segmentoLabel: 'Segmento',
        segmentoPlaceholder: 'Ex.: SaaS, Serviços, Indústria',
        minFitScoreLabel: 'Score de fit mínimo',
        minFitScoreHint: '0–100. Deixe vazio para não filtrar por score.',
        cadenceLabel: '4. Política de outreach',
        maxAttempts: 'Máx. tentativas',
        cooldownDays: 'Cooldown (dias)',
        maxPerDay: 'Máx. calls/dia',
        channelLabel: 'Canal',
        channelSdrVoice: 'SDR Voice (discador IA)',
        scriptLabel: '5. Talk track',
        scriptPlaceholder: 'Template padrão "Cross-sell consultivo"',
        scriptHint: 'ID do template de script (opcional).',
        // Reach calculator
        reachTitle: 'Alcance estimado',
        reachButton: 'Calcular alcance',
        reachRecalc: 'Recalcular',
        reachResult: '{{accounts}} contas elegíveis · ~{{contacts}} contatos',
        reachEmpty: 'Calcule o alcance antes de ativar.',
        reachError: 'Não foi possível calcular o alcance. Tente de novo.',
        reachNeedsProduct: 'Selecione o produto para calcular o alcance.',
        // Actions
        saveDraft: 'Salvar rascunho',
        activate: 'Ativar campanha',
        saveSuccess: 'Rascunho salvo.',
        saveError: 'Não foi possível salvar a campanha.',
        activateConfirmTitle: 'Ativar campanha?',
        activateConfirmBody:
          'Ao ativar, o sistema tira um snapshot dos membros elegíveis e começa a ' +
          'enfileirar as ligações. As primeiras calls saem em até 5 minutos.',
        activateConfirmOk: 'Ativar e iniciar ligações',
        activateSuccess: 'Campanha ativada · {{count}} membros enfileirados.',
        activateError: 'Não foi possível ativar a campanha.',
        activateNeedsSave: 'Salve o rascunho antes de ativar.',
      },
      // Funnel detail (UX §4.8)
      detail: {
        funnelTitle: 'Funnel',
        live: 'Tempo real',
        stages: {
          members: 'Membros',
          attempts: 'Tentativas',
          answered: 'Atendeu',
          interest: 'Com interesse',
          meeting: 'Reunião marcada',
          opportunity: 'Oportunidade',
          won: 'Won',
        },
        membersTitle: 'Membros',
        viewMembers: 'Ver lista de membros',
        pause: 'Pausar campanha',
        pauseConfirmTitle: 'Pausar campanha?',
        pauseConfirmBody: 'As ligações pendentes deixam de ser enfileiradas. Você pode reativar depois.',
        pauseSuccess: 'Campanha pausada.',
        pauseError: 'Não foi possível pausar a campanha.',
        loadError: 'Não foi possível carregar a campanha.',
        memberColumns: {
          account: 'Conta',
          cnpj: 'CNPJ',
          status: 'Status',
          outcome: 'Outcome',
          attempts: 'Tentativas',
        },
        membersEmpty: 'Nenhum membro neste status.',
        statusFilterAll: 'Todos os status',
        memberStatus: {
          queued: 'Na fila',
          dispatched: 'Disparado',
          contacted: 'Contatado',
          responded: 'Respondeu',
          converted: 'Convertido',
          disqualified: 'Desqualificado',
          opted_out: 'Opt-out',
          failed: 'Falhou',
        },
      },
      // SDR cockpit context card (UX §4.9)
      callContext: {
        badge: 'Cross-Sell',
        title: 'Contexto',
        decisor: 'Decisor',
        alreadyClient: 'Já é cliente de:',
        willOffer: 'Vai oferecer:',
        alternatives: 'Alternativas (em ordem):',
        talkTrack: 'Talk track sugerido',
        noTalkTrack: 'Sem talk track sugerido.',
      },
      // Member outcome (PRD F-CS-03.2 / F-CS-05.1)
      outcome: {
        interesse: 'Tem interesse',
        agendou: 'Agendou reunião',
        recusou: 'Recusou',
        nao_atendeu: 'Não atendeu',
        numero_errado: 'Número errado',
      },
    },
    import: {
      title: 'Importar base de clientes',
      subtitle:
        'Suba sua base (clientes, produtos contratados e decisores) para ' +
        'descobrir oportunidades de cross-sell.',
      // Drop zone
      dropTitle: 'Arraste seu arquivo aqui ou clique para selecionar',
      dropHint: 'Aceito .xlsx ou .csv, até 50MB.',
      dropButton: 'Selecionar arquivo',
      uploading: 'Enviando arquivo…',
      validating: 'Validando arquivo…',
      // Empty-catalog warning (sponsor requirement)
      emptyCatalogTitle: 'Cadastre seus produtos primeiro',
      emptyCatalogBody:
        'Você ainda não tem produtos no catálogo. SKUs sem correspondência ' +
        'não viram produtos contratados — cadastre seus produtos antes de importar.',
      emptyCatalogCta: 'Ir para o catálogo de produtos',
      emptyCatalogProceed: 'Importar mesmo assim',
      // Preview
      preview: {
        title: 'Pré-visualização',
        uploadedBy: 'subido por {{name}}',
        validRows: '{{count}} linhas válidas',
        errorRows: '{{count}} com erro',
        warnings: '{{count}} com avisos',
        uniqueCompanies: 'Empresas únicas: {{count}}',
        assets: 'Produtos (Assets): {{count}}',
        decisores: 'Decisores: {{count}}',
        sampleTitle: '5 primeiras linhas válidas',
        errorsTitle: 'Linhas com erro',
        skuWarning:
          'SKU não encontrado no catálogo — esta linha não virará produto contratado.',
        confirm: 'Confirmar import',
        cancel: 'Cancelar',
        noPreview: 'Aguardando validação…',
      },
      // LGPD consent modal
      consent: {
        title: 'Consentimento LGPD',
        intro:
          'Esse arquivo contém {{count}} decisores com dados pessoais ' +
          '(nome, cargo, email, telefone).',
        ask: 'Pra processar, preciso da sua confirmação:',
        legalBasisLabel: 'Base legal para o tratamento',
        legalBasisRequired: 'Base legal é obrigatória.',
        checkbox:
          'Confirmo que tenho a base legal acima para compartilhar esses dados ' +
          'com a Janus. Reconheço que vão ser usados pra outreach comercial, ' +
          'enriquecimento e analytics.',
        perTenantNote:
          'Consent por tenant: esse consent vale APENAS para este workspace. ' +
          'Se outro workspace da sua conta subir os mesmos decisores, cada um ' +
          'terá seu próprio consent independente.',
        accept: 'Aceitar e processar',
        cancel: 'Cancelar',
        legalBasis: {
          legitimo_interesse: 'Legítimo interesse',
          consentimento: 'Consentimento do titular',
          contrato_servico: 'Execução de contrato',
          obrigacao_legal: 'Cumprimento de obrigação legal',
        },
        legalBasisHint: {
          legitimo_interesse:
            'Default em B2B. Tratamento necessário para finalidade legítima da ' +
            'minha empresa (cross-sell em base ativa). Art. 7 IX.',
          consentimento:
            'Tenho consent explícito e individual dos decisores listados ' +
            '(campanha opt-in). Art. 7 I.',
          contrato_servico:
            'Dados originados de contrato em vigor com cada empresa listada. Art. 7 V.',
          obrigacao_legal:
            'Tratamento determinado por lei ou regulação setorial. Art. 7 II.',
        },
      },
      // Progress / confirmed
      progress: {
        title: 'Processando import',
        confirmedTitle: 'Aguardando processamento',
        consentRegistered: 'Consent registrado',
        legalBasisLabel: 'Base legal: {{value}}',
        decisoresCovered: 'Decisores cobertos: {{count}}',
        waitingWorker: 'Esperando o worker pegar o job…',
        waitingWorkerHint: 'Normalmente menos de 30 segundos.',
        leaveOk: 'Você pode sair dessa tela. Vou te avisar quando começar e quando terminar.',
        backToDashboard: 'Voltar pro dashboard',
        eta: 'ETA: ~{{value}}',
        etaCalculating: 'Calculando…',
        elapsed: 'Tempo decorrido: {{value}}',
        statusLabel: {
          queued: 'Aguardando início',
          validating: 'Validando arquivo',
          preview_ready: 'Pronto para revisão',
          confirmed: 'Aguardando worker (queued)',
          processing: 'Processando',
          completed: 'Concluído',
          failed: 'Falhou',
          cancelled: 'Cancelado',
        },
        connectionUnstable: 'Conexão instável · última atualização há {{seconds}}s',
        cancelConfirmTitle: 'Cancelar import?',
        cancelConfirmProcessing:
          'As empresas já importadas permanecem na base; o que ainda não foi ' +
          'processado será descartado.',
        cancelConfirmDefault: 'O job será cancelado e o arquivo apagado em 7 dias.',
      },
      // Completion
      done: {
        completedTitle: '✅ Import concluído',
        failedTitle: '❌ Import falhou',
        summary:
          '{{accounts}} empresas, {{assets}} produtos, {{decisores}} decisores prontos.',
        viewBase: 'Ver minha base →',
      },
      // Duplicate 409 modal
      duplicate: {
        title: 'Esse arquivo já foi processado',
        processedAt: 'Hash idêntico, processado em {{date}}',
        statusCompleted: '✅ Concluído',
        statusFailed: '❌ Falhou',
        statusProcessing: '🔄 Em andamento',
        viewExisting: 'Ver o import existente',
        viewExistingHint: 'Abre o import já processado.',
        force: 'Forçar reprocessamento',
        forceHint:
          'Roda de novo (útil se você corrigiu dados no CRM source). Não ' +
          'duplica accounts/assets — reconciliação com o que já existe.',
        forceConfirmTitle: 'Tem certeza?',
        forceConfirmBody: 'Vai criar um novo job em paralelo. Re-suba o arquivo para confirmar.',
        forceNoPermission: 'Apenas admins podem forçar reprocessamento.',
        cancel: 'Cancelar',
      },
      recent: {
        title: 'Imports recentes',
        empty: 'Nenhum import ainda. Suba sua primeira base acima.',
        columns: {
          file: 'Arquivo',
          status: 'Status',
          rows: 'Linhas',
          uploadedBy: 'Subido por',
          when: 'Quando',
        },
      },
    },
    // Whitespace tab on the account detail page
    whitespace: {
      tab: 'Cross-Sell',
      hasTitle: 'Já tem',
      eligibleTitle: 'A oferecer',
      optedOutTitle: 'Opt-outs do titular',
      excludedTitle: 'Excluídos comercialmente',
      hasEmpty: 'Nenhum produto contratado mapeado ainda.',
      eligibleEmpty: 'Nenhuma oportunidade de cross-sell no momento.',
      scoreBadge: 'Score {{score}}',
      scoreBadgeUnranked: 'Recomendado',
      scoreReasonsTitle: 'Por que oferecer:',
      exclude: 'Excluir da base',
      excludeConfirm: 'Excluir este produto das ofertas para esta conta?',
      excluded: 'Excluído da base',
      reinclude: 'Reincluir',
      excludeReasonPlaceholder: 'Motivo (opcional)',
      optedOutBadge: 'Opt-out registrado em {{date}} · {{source}}',
      optedOutNote: 'Não pode ser reativado pela UI. Contate o DPO.',
      excludedBadge: 'Excluído em {{date}}',
      loadError: 'Não foi possível carregar o whitespace.',
      excludeSuccess: 'Produto excluído da base desta conta.',
      reincludeSuccess: 'Produto reincluído.',
      excludePendingHint: 'Removendo… A lista pode levar até 30s para refletir.',
    },
    // Global queue drawer + badge
    queue: {
      iconLabel: 'Jobs de Cross-Sell',
      drawerTitle: 'Jobs em andamento',
      empty: 'Nenhum job em andamento.',
      viewDetail: 'Ver detalhe →',
      dismiss: 'Dispensar',
      offlineWarning:
        'Você ficou offline — alguns eventos podem ter sido perdidos.',
      reconnect: 'Recarregar para reconectar',
      jobImporting: 'Importando {{label}}',
      remaining: '{{value}} restantes',
    },
    errors: {
      IMPORT_INVALID_CNPJ: "CNPJ '{{val}}' parece inválido — confira o dígito verificador.",
      IMPORT_MISSING_REQUIRED_COLUMN: "Coluna obrigatória '{{col}}' não encontrada no arquivo.",
      IMPORT_FILE_TOO_LARGE: 'Arquivo passou de 50MB. Quebre em arquivos menores ou comprima.',
      IMPORT_INVALID_FORMAT: 'Aceito apenas .xlsx ou .csv. Esse arquivo é {{ext}}.',
      IMPORT_DUPLICATE_HASH: 'Esse arquivo já foi processado.',
      IMPORT_CONSENT_REQUIRED:
        'Esse arquivo tem decisores. Aceite o consent LGPD pra continuar.',
      IMPORT_INVALID_LEGAL_BASIS:
        'Base legal não reconhecida. Escolha uma das opções do dropdown.',
      MISSING_TENANT_CONSENT:
        'Não há consent ativo desse decisor para esse workspace. Re-suba com consent.',
      IMPORT_PROCESSING_FAILED:
        'Algo deu errado no processamento. Você pode tentar de novo. Se persistir, abra um chamado.',
      UNAUTHORIZED: 'Sua sessão expirou. Faça login de novo.',
      FORBIDDEN: 'Você não tem permissão pra essa ação. Fale com seu admin.',
      generic: 'Algo deu errado. Tente novamente.',
    },
  },
} as const;

export const CROSS_SELL_EN_US = {
  crossSell: {
    title: 'Cross-Sell',
    sidebar: 'Import clients',
    nav: {
      import: 'Import base',
    },
    campaignsSidebar: 'Campaigns',
    catalogSidebar: 'Import catalog',
    campaigns: {
      title: 'Cross-sell campaigns',
      subtitle: 'Create and track campaigns that offer products to your active base.',
      newButton: 'New campaign',
      empty: 'No campaigns yet. Create your first one to start offering.',
      columns: {
        name: 'Name',
        product: 'Product',
        status: 'Status',
        reach: 'Reach',
        members: 'Members',
        createdBy: 'Created by',
        when: 'Created on',
      },
      status: {
        draft: 'Draft',
        active: 'Active',
        paused: 'Paused',
        completed: 'Completed',
        cancelled: 'Cancelled',
      },
      wizard: {
        title: 'New cross-sell campaign',
        subtitle: 'Define the product, the audience and the outreach policy.',
        nameLabel: '1. Campaign name',
        namePlaceholder: 'Cross-sell Janus SDR Voice Q3 2026',
        nameRequired: 'Give the campaign a name.',
        descriptionLabel: 'Description (optional)',
        productLabel: '2. Product to offer',
        productPlaceholder: 'Pick a product from the catalog',
        productRequired: 'Choose the product to offer.',
        productCatalogEmpty:
          "You don't have any products in your catalog yet. Register your products first.",
        productCatalogCta: 'Go to catalog',
        filtersLabel: '3. Eligibility filters',
        porteLabel: 'Size',
        portePlaceholder: 'Small (P) · Medium (M) · Large (G)',
        segmentoLabel: 'Segment',
        segmentoPlaceholder: 'e.g. SaaS, Services, Industry',
        minFitScoreLabel: 'Minimum fit score',
        minFitScoreHint: '0–100. Leave empty to skip the score filter.',
        cadenceLabel: '4. Outreach policy',
        maxAttempts: 'Max attempts',
        cooldownDays: 'Cooldown (days)',
        maxPerDay: 'Max calls/day',
        channelLabel: 'Channel',
        channelSdrVoice: 'SDR Voice (AI dialer)',
        scriptLabel: '5. Talk track',
        scriptPlaceholder: 'Default template "Consultative cross-sell"',
        scriptHint: 'Script template ID (optional).',
        reachTitle: 'Estimated reach',
        reachButton: 'Calculate reach',
        reachRecalc: 'Recalculate',
        reachResult: '{{accounts}} eligible accounts · ~{{contacts}} contacts',
        reachEmpty: 'Calculate the reach before activating.',
        reachError: 'Could not calculate the reach. Try again.',
        reachNeedsProduct: 'Select the product to calculate the reach.',
        saveDraft: 'Save draft',
        activate: 'Activate campaign',
        saveSuccess: 'Draft saved.',
        saveError: 'Could not save the campaign.',
        activateConfirmTitle: 'Activate campaign?',
        activateConfirmBody:
          'On activation, the system snapshots the eligible members and starts ' +
          'enqueuing the calls. The first calls go out within 5 minutes.',
        activateConfirmOk: 'Activate and start calls',
        activateSuccess: 'Campaign activated · {{count}} members enqueued.',
        activateError: 'Could not activate the campaign.',
        activateNeedsSave: 'Save the draft before activating.',
      },
      detail: {
        funnelTitle: 'Funnel',
        live: 'Live',
        stages: {
          members: 'Members',
          attempts: 'Attempts',
          answered: 'Answered',
          interest: 'Interested',
          meeting: 'Meeting booked',
          opportunity: 'Opportunity',
          won: 'Won',
        },
        membersTitle: 'Members',
        viewMembers: 'View member list',
        pause: 'Pause campaign',
        pauseConfirmTitle: 'Pause campaign?',
        pauseConfirmBody: 'Pending calls stop being enqueued. You can reactivate later.',
        pauseSuccess: 'Campaign paused.',
        pauseError: 'Could not pause the campaign.',
        loadError: 'Could not load the campaign.',
        memberColumns: {
          account: 'Account',
          cnpj: 'CNPJ',
          status: 'Status',
          outcome: 'Outcome',
          attempts: 'Attempts',
        },
        membersEmpty: 'No members in this status.',
        statusFilterAll: 'All statuses',
        memberStatus: {
          queued: 'Queued',
          dispatched: 'Dispatched',
          contacted: 'Contacted',
          responded: 'Responded',
          converted: 'Converted',
          disqualified: 'Disqualified',
          opted_out: 'Opted out',
          failed: 'Failed',
        },
      },
      callContext: {
        badge: 'Cross-Sell',
        title: 'Context',
        decisor: 'Decision-maker',
        alreadyClient: 'Already a client of:',
        willOffer: 'Will offer:',
        alternatives: 'Alternatives (in order):',
        talkTrack: 'Suggested talk track',
        noTalkTrack: 'No suggested talk track.',
      },
      outcome: {
        interesse: 'Interested',
        agendou: 'Booked a meeting',
        recusou: 'Declined',
        nao_atendeu: 'No answer',
        numero_errado: 'Wrong number',
      },
    },
    import: {
      title: 'Import customer base',
      subtitle:
        'Upload your base (customers, owned products and decision-makers) to ' +
        'discover cross-sell opportunities.',
      dropTitle: 'Drag your file here or click to select',
      dropHint: 'Accepts .xlsx or .csv, up to 50MB.',
      dropButton: 'Select file',
      uploading: 'Uploading file…',
      validating: 'Validating file…',
      emptyCatalogTitle: 'Register your products first',
      emptyCatalogBody:
        "You don't have any products in your catalog yet. SKUs without a match " +
        "won't become owned products — register your products before importing.",
      emptyCatalogCta: 'Go to product catalog',
      emptyCatalogProceed: 'Import anyway',
      preview: {
        title: 'Preview',
        uploadedBy: 'uploaded by {{name}}',
        validRows: '{{count}} valid rows',
        errorRows: '{{count}} with errors',
        warnings: '{{count}} with warnings',
        uniqueCompanies: 'Unique companies: {{count}}',
        assets: 'Products (Assets): {{count}}',
        decisores: 'Decision-makers: {{count}}',
        sampleTitle: 'First 5 valid rows',
        errorsTitle: 'Rows with errors',
        skuWarning:
          "SKU not found in catalog — this row won't become an owned product.",
        confirm: 'Confirm import',
        cancel: 'Cancel',
        noPreview: 'Waiting for validation…',
      },
      consent: {
        title: 'LGPD consent',
        intro:
          'This file contains {{count}} decision-makers with personal data ' +
          '(name, role, email, phone).',
        ask: 'To process it, I need your confirmation:',
        legalBasisLabel: 'Legal basis for processing',
        legalBasisRequired: 'Legal basis is required.',
        checkbox:
          'I confirm I have the legal basis above to share this data with ' +
          'Janus. I acknowledge it will be used for commercial outreach, ' +
          'enrichment and analytics.',
        perTenantNote:
          'Per-tenant consent: this consent applies ONLY to this workspace. ' +
          'If another workspace of your account uploads the same decision-makers, ' +
          'each will have its own independent consent.',
        accept: 'Accept and process',
        cancel: 'Cancel',
        legalBasis: {
          legitimo_interesse: 'Legitimate interest',
          consentimento: 'Data subject consent',
          contrato_servico: 'Contract execution',
          obrigacao_legal: 'Legal obligation',
        },
        legalBasisHint: {
          legitimo_interesse:
            'B2B default. Processing necessary for my company\'s legitimate ' +
            'purpose (cross-sell on active base). Art. 7 IX.',
          consentimento:
            'I have explicit, individual consent from the listed decision-makers ' +
            '(opt-in campaign). Art. 7 I.',
          contrato_servico:
            'Data originated from an active contract with each listed company. Art. 7 V.',
          obrigacao_legal: 'Processing required by law or sector regulation. Art. 7 II.',
        },
      },
      progress: {
        title: 'Processing import',
        confirmedTitle: 'Waiting to process',
        consentRegistered: 'Consent registered',
        legalBasisLabel: 'Legal basis: {{value}}',
        decisoresCovered: 'Decision-makers covered: {{count}}',
        waitingWorker: 'Waiting for the worker to pick up the job…',
        waitingWorkerHint: 'Usually less than 30 seconds.',
        leaveOk: "You can leave this screen. I'll notify you when it starts and finishes.",
        backToDashboard: 'Back to dashboard',
        eta: 'ETA: ~{{value}}',
        etaCalculating: 'Calculating…',
        elapsed: 'Elapsed: {{value}}',
        statusLabel: {
          queued: 'Waiting to start',
          validating: 'Validating file',
          preview_ready: 'Ready for review',
          confirmed: 'Waiting for worker (queued)',
          processing: 'Processing',
          completed: 'Completed',
          failed: 'Failed',
          cancelled: 'Cancelled',
        },
        connectionUnstable: 'Unstable connection · last update {{seconds}}s ago',
        cancelConfirmTitle: 'Cancel import?',
        cancelConfirmProcessing:
          'Companies already imported stay in your base; whatever has not been ' +
          'processed yet will be discarded.',
        cancelConfirmDefault: 'The job will be cancelled and the file deleted in 7 days.',
      },
      done: {
        completedTitle: '✅ Import completed',
        failedTitle: '❌ Import failed',
        summary:
          '{{accounts}} companies, {{assets}} products, {{decisores}} decision-makers ready.',
        viewBase: 'View my base →',
      },
      duplicate: {
        title: 'This file was already processed',
        processedAt: 'Identical hash, processed on {{date}}',
        statusCompleted: '✅ Completed',
        statusFailed: '❌ Failed',
        statusProcessing: '🔄 In progress',
        viewExisting: 'View the existing import',
        viewExistingHint: 'Opens the already-processed import.',
        force: 'Force reprocessing',
        forceHint:
          'Runs again (useful if you corrected data in the CRM source). Does not ' +
          'duplicate accounts/assets — reconciles with what already exists.',
        forceConfirmTitle: 'Are you sure?',
        forceConfirmBody: "It will create a new parallel job. Re-upload the file to confirm.",
        forceNoPermission: 'Only admins can force reprocessing.',
        cancel: 'Cancel',
      },
      recent: {
        title: 'Recent imports',
        empty: 'No imports yet. Upload your first base above.',
        columns: {
          file: 'File',
          status: 'Status',
          rows: 'Rows',
          uploadedBy: 'Uploaded by',
          when: 'When',
        },
      },
    },
    whitespace: {
      tab: 'Cross-Sell',
      hasTitle: 'Already has',
      eligibleTitle: 'To offer',
      optedOutTitle: 'Subject opt-outs',
      excludedTitle: 'Commercially excluded',
      hasEmpty: 'No owned products mapped yet.',
      eligibleEmpty: 'No cross-sell opportunities right now.',
      scoreBadge: 'Score {{score}}',
      scoreBadgeUnranked: 'Recommended',
      scoreReasonsTitle: 'Why offer this:',
      exclude: 'Exclude from base',
      excludeConfirm: 'Exclude this product from offers for this account?',
      excluded: 'Excluded from base',
      reinclude: 'Re-include',
      excludeReasonPlaceholder: 'Reason (optional)',
      optedOutBadge: 'Opt-out registered on {{date}} · {{source}}',
      optedOutNote: 'Cannot be reactivated via UI. Contact the DPO.',
      excludedBadge: 'Excluded on {{date}}',
      loadError: 'Could not load the whitespace.',
      excludeSuccess: 'Product excluded from this account base.',
      reincludeSuccess: 'Product re-included.',
      excludePendingHint: 'Removing… The list can take up to 30s to reflect.',
    },
    queue: {
      iconLabel: 'Cross-Sell jobs',
      drawerTitle: 'Jobs in progress',
      empty: 'No jobs in progress.',
      viewDetail: 'View detail →',
      dismiss: 'Dismiss',
      offlineWarning: 'You went offline — some events may have been lost.',
      reconnect: 'Reload to reconnect',
      jobImporting: 'Importing {{label}}',
      remaining: '{{value}} remaining',
    },
    errors: {
      IMPORT_INVALID_CNPJ: "CNPJ '{{val}}' looks invalid — check the check digit.",
      IMPORT_MISSING_REQUIRED_COLUMN: "Required column '{{col}}' not found in the file.",
      IMPORT_FILE_TOO_LARGE: 'File exceeds 50MB. Split into smaller files or compress.',
      IMPORT_INVALID_FORMAT: 'Only .xlsx or .csv are accepted. This file is {{ext}}.',
      IMPORT_DUPLICATE_HASH: 'This file was already processed.',
      IMPORT_CONSENT_REQUIRED: 'This file has decision-makers. Accept the LGPD consent to continue.',
      IMPORT_INVALID_LEGAL_BASIS: 'Legal basis not recognised. Pick one of the dropdown options.',
      MISSING_TENANT_CONSENT:
        'No active consent for this decision-maker in this workspace. Re-upload with consent.',
      IMPORT_PROCESSING_FAILED:
        'Something went wrong while processing. You can try again. If it persists, open a ticket.',
      UNAUTHORIZED: 'Your session expired. Please log in again.',
      FORBIDDEN: "You don't have permission for this action. Talk to your admin.",
      generic: 'Something went wrong. Please try again.',
    },
  },
} as const;

let registered = false;

/** Registers cross-sell translations into the i18next runtime. Idempotent. */
export function registerCrossSellTranslations(): void {
  if (registered) return;
  i18n.addResourceBundle('pt-BR', 'translation', CROSS_SELL_PT_BR, /* deep */ true, /* overwrite */ true);
  i18n.addResourceBundle('en-US', 'translation', CROSS_SELL_EN_US, /* deep */ true, /* overwrite */ true);
  registered = true;
}
